import {h, Fragment} from "preact"
import { useSort } from "@nosto/preact"
import { useEffect, useState, useRef } from "preact/hooks";
import { sortOptions } from "../config"

export default function SortCustomDropdown() {
    const { activeSort, setSort } = useSort(sortOptions);
    const [ sortOpen, setSortOpen] = useState(false);
    const [ focusIndex, setFocusIndex ] = useState(sortOptions.findIndex(opt => opt.id === activeSort) ?? 0);
    const dropdownRef = useRef(null);
    const toggleRef = useRef(null);

    useEffect(() => {
        if (sortOpen && dropdownRef.current && dropdownRef.current.children.length) {
            const selectedElement = dropdownRef.current.children[focusIndex];
            if (selectedElement) {
                setTimeout(() => {
                    selectedElement.focus();
                }, 50)
            }
        }
    }, [ sortOpen, focusIndex ]);

    useEffect(() => { // keyboard controls
        const handleKeydown = (event) => {
            switch(event.key) {
                case "Escape":
                case "Tab" :
                    setSortOpen(false);
                    toggleRef?.current?.focus();
                    break;
                case "ArrowUp":
                    event.preventDefault();
                    setFocusIndex(prevFocusIndex => (prevFocusIndex > 0) ? prevFocusIndex - 1 : sortOptions.length - 1)
                    break;
                case "ArrowDown":
                    event.preventDefault();
                    setFocusIndex(prevFocusIndex => (prevFocusIndex < (sortOptions.length - 1)) ? prevFocusIndex + 1 : 0)
                    break;
                case "Enter":
                    event.preventDefault();
                    const optionId = dropdownRef.current.children[focusIndex].getAttribute('data-id');
                    setSort(optionId);
                    setSortOpen(false);
                    toggleRef?.current?.focus();
                    break;
                default:
                    break;
            }
        }
        if(sortOpen && dropdownRef.current) {
            window.document.addEventListener('keydown', handleKeydown)
            return () => window.document.removeEventListener('keydown', handleKeydown)
        }
    }, [ sortOpen, focusIndex ])
    
    useEffect(() => { // close dropdown on outside clicks
        const handleClick = (event) => {
            if(
                !dropdownRef.current.contains(event.target) && 
                !toggleRef.current.contains(event.target)
            ) {
                setSortOpen(false);
                toggleRef?.current?.focus();
            };
        };

        if(sortOpen && dropdownRef?.current && toggleRef?.current) {
            window.document.addEventListener('click', handleClick);
            return () => window.document.removeEventListener('click', handleClick);
        };
    }, [ sortOpen ]);

    const handleOptionClick = (event) => {
        const optionId = event.target.getAttribute('data-id');
        setSort(optionId);
        setFocusIndex(sortOptions.findIndex(opt => opt.id === optionId));
        setSortOpen(false);
        toggleRef?.current?.focus();
    };

    const toggleSort = () => {
        setSortOpen(prevSortOpen => !prevSortOpen);
        setFocusIndex(0);
    };

    return (
        <div class="ns-sort-custom-wrap ns-relative">
            <div class="ns-sort-toggle-wrap">
                <button 
                    type="button"
                    class="ns-sort-toggle ns-flex ns-flex-nowrap ns-items-center"
                    aria-haspopup="listbox"
                    aria-controls="ns-sort-dropdown"
                    aria-labelledby="ns-sort-header"
                    aria-expanded={sortOpen}
                    tabIndex="0"
                    onClick={toggleSort}
                    ref={toggleRef}
                >
                    <h4 id="ns-sort-header" class="ns-sort-header ns-flex ns-flex-col ns-items-end">
                        Sort By
                    </h4>
                    <svg class="ns-block" aria-hidden="true" role="presentation" width="8" height="6" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M-0.00146484 0C-0.00146484 0 3.82952 6 3.9422 6C4.16755 6 7.99854 0 7.99854 0H-0.00146484Z" fill="black"/>
                    </svg>
                </button>
            </div>
            <div>
                <ul
                    class="ns-list-unstyled ns-absolute ns-flex ns-flex-col"
                    id="ns-sort-dropdown"
                    aria-labelledby="ns-sort-header"
                    aria-hidden={!sortOpen}
                    ref={dropdownRef}
                    role="listbox"
                    tabIndex="-1"
                >
                    {sortOptions.map((option, i) => (
                        <li
                            class="ns-sort-list-option ns-relative ns-flex ns-flex-nowrap ns-items-center ns-justify-end"
                            role="option"
                            data-id={option.id}
                            onClick={handleOptionClick}
                            aria-selected={(activeSort === option.id)}
                            tabIndex={i === focusIndex ? "0" : "-1"}
                            key={option.id}
                            style={{ '--ns-delay': `${(i + 1) * 0.05}s` }}
                        >
                            {option.value.name}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    )
}